import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { Calendar, Tag, TicketIcon, User } from "lucide-react";
import { Badge } from "../../ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../ui/tooltip";

import type { Ticket } from "@cohesive/api-entities";
import {
  BaseEntityCard,
  BaseEntityCardActions,
  BaseEntityCardBody,
  BaseEntityCardContent,
  BaseEntityCardContextMenu,
  BaseEntityCardDescription,
  BaseEntityCardExternalLinkAction,
  BaseEntityCardHeader,
  BaseEntityCardIcon,
  BaseEntityCardMentionAction,
  BaseEntityCardMetadata,
  BaseEntityCardMetadataItem,
  BaseEntityCardProps,
  BaseEntityCardTags,
  BaseEntityCardTitle,
} from "../BaseCard";

type TicketCardProps = Ticket & BaseEntityCardProps;

// Avatar component for TicketCard
export function TicketCardAvatar({
  className,
  style,
}: Partial<TicketCardProps>) {
  return (
    <BaseEntityCardIcon className={className} style={style}>
      <TicketIcon className="h-5 w-5 text-muted-foreground" />
    </BaseEntityCardIcon>
  );
}

export function TicketCard({
  platform,
  name,
  content = "",
  category,
  priority,
  pipeline,
  status,
  owner_name,
  created_by_name,
  created_at,
  updated_at,
  web_link,
  labels,
  onClick,
  onDoubleClick,
  onMentionClick,
  actions,
  processes,
  className,
  style,
}: TicketCardProps) {
  const formattedCreatedDate = created_at
    ? formatDistanceToNow(new Date(created_at), { addSuffix: true })
    : "";

  const formattedUpdatedDate = updated_at
    ? formatDistanceToNow(new Date(updated_at), { addSuffix: true })
    : "";

  const contentPreview =
    content && content.length > 100
      ? `${content.substring(0, 100)}...`
      : content;

  // Map priority to color
  const getPriorityColor = (priority?: string) => {
    if (!priority) return "bg-neutral-100 text-neutral-800";

    const lowerPriority = priority.toLowerCase();
    if (
      lowerPriority.includes("high") ||
      lowerPriority.includes("critical") ||
      lowerPriority === "1"
    ) {
      return "bg-red-100 text-red-800";
    } else if (lowerPriority.includes("medium") || lowerPriority === "2") {
      return "bg-amber-100 text-amber-800";
    } else if (lowerPriority.includes("low") || lowerPriority === "3") {
      return "bg-spectrum-green-light/30 text-green-800";
    }
    return "bg-neutral-100 text-neutral-800";
  };

  // Map status to color
  const getStatusColor = (status?: string) => {
    if (!status) return "bg-neutral-100 text-neutral-800";

    const lowerStatus = status.toLowerCase();
    if (
      lowerStatus.includes("done") ||
      lowerStatus.includes("complete") ||
      lowerStatus.includes("resolved") ||
      lowerStatus.includes("closed")
    ) {
      return "bg-spectrum-green-light/30 text-green-800";
    } else if (
      lowerStatus.includes("progress") ||
      lowerStatus.includes("review") ||
      lowerStatus.includes("open")
    ) {
      return "bg-spectrum-blue-light/30 text-teal";
    } else if (lowerStatus.includes("block") || lowerStatus.includes("hold")) {
      return "bg-red-100 text-red-800";
    } else if (
      lowerStatus.includes("backlog") ||
      lowerStatus.includes("todo") ||
      lowerStatus.includes("pending")
    ) {
      return "bg-neutral-100 text-neutral-800";
    }
    return "bg-plum/10 text-purple-800";
  };

  return (
    <BaseEntityCard
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      processes={processes}
      className={className}
      style={style}
    >
      <TicketCardAvatar />

      <BaseEntityCardContent>
        <BaseEntityCardHeader>
          <BaseEntityCardTitle platform={platform}>{name}</BaseEntityCardTitle>
          <BaseEntityCardActions>
            {web_link && (
              <BaseEntityCardExternalLinkAction
                href={web_link}
                tooltip="Open ticket"
              />
            )}
            {onMentionClick && (
              <BaseEntityCardMentionAction onClick={onMentionClick} />
            )}
            {actions && actions.length > 0 && (
              <BaseEntityCardContextMenu actions={actions} />
            )}
          </BaseEntityCardActions>
        </BaseEntityCardHeader>

        <BaseEntityCardBody>
          {contentPreview && (
            <BaseEntityCardDescription className="line-clamp-2 text-foreground">
              {contentPreview}
            </BaseEntityCardDescription>
          )}

          <BaseEntityCardTags>
            {(owner_name || created_by_name) && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <User className="h-3 w-3" />
                <span>{owner_name || created_by_name}</span>
              </div>
            )}

            {status && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant="outline"
                      className={cn("text-xs", getStatusColor(status))}
                    >
                      {status}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Status</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {priority && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant="outline"
                      className={cn("text-xs", getPriorityColor(priority))}
                    >
                      {priority}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Priority</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {category && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant="outline"
                      className="text-xs bg-plum/10 text-purple-800"
                    >
                      {category}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Category</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {pipeline && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant="outline"
                      className="text-xs bg-spectrum-blue-light/30 text-teal"
                    >
                      {pipeline}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Pipeline</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {labels && labels.length > 0 && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Tag className="h-3 w-3" />
                <span>
                  {labels.slice(0, 2).join(", ")}
                  {labels.length > 2 ? "..." : ""}
                </span>
              </div>
            )}
          </BaseEntityCardTags>

          {(formattedCreatedDate || formattedUpdatedDate) && (
            <BaseEntityCardMetadata>
              {formattedCreatedDate && (
                <BaseEntityCardMetadataItem
                  icon={<Calendar className="h-3 w-3" />}
                >
                  Created {formattedCreatedDate}
                </BaseEntityCardMetadataItem>
              )}
              {formattedUpdatedDate &&
                formattedUpdatedDate !== formattedCreatedDate && (
                  <span>Updated {formattedUpdatedDate}</span>
                )}
            </BaseEntityCardMetadata>
          )}
        </BaseEntityCardBody>
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}
